import React, { createContext, ReactNode, useContext, useState } from 'react';
import useMobileScreen from '../hooks/useIsMobile';
import { ApiResponseType, IBlockDetailsCountType, IBlockIdType, IBlocksType, IPayloadType } from './ContextType';
import { axiosInstance } from '../api';
import moment from 'moment';

type IBlocksContext = {
  blocksData: IBlocksType[];
  getBlocksData: (payload: IPayloadType) => void;
  getTransactionCountByBlockNum: (blocknum: string) => void;
  isTotalPages?: number;
  loading: boolean;
  blockDetailsCount?: IBlockDetailsCountType;
  getTransactionsByBlockId: (payload: IPayloadType) => void;
  getBlocksDataPerMinute: () => void;
  blockCountPerMinute: number;
  transactionsByBlockId: IBlockIdType[];
  isIdTotalPages?: number;
  totalCount: number
}

const BlocksContext = createContext<IBlocksContext | undefined>(undefined);

export const useBlocksContext = () => {
  const context = useContext(BlocksContext);
  if (!context) {
    throw new Error('useTheme must be used within a CustomToastProvider');
  }
  return context;
};

type IContextProviderProps = {
  children: ReactNode;
};

export const BlocksContextProvider: React.FC<IContextProviderProps> = ({children}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [blocksData, setBlocksData] = useState<IBlocksType[]>([]);
  const [isTotalPages, setIsTotalPages] = useState<number>();
  const [blockDetailsCount, setBlockDetailsCount] = useState<IBlockDetailsCountType>();
  const [transactionsByBlockId, setTransactionsByBlockId] = useState<IBlockIdType[]>([]);
  const [isIdTotalPages, setIsIdTotalPages] = useState<number>();
  const [totalCount, setTotalCount] = useState<number>(0);
  const [blockCountPerMinute, setBlockCountPerMinute] = useState<number>(0);
  const mobile = useMobileScreen();

  const getBlocksDataPerMinute = async (): Promise<ApiResponseType<any>> => {
    try {
      setLoading(true);
      let url = `/metrics/blocksByLastMinute`;
      const response = await axiosInstance.get(url);
      setBlockCountPerMinute(response?.data?.block_count);
      setLoading(false);
      return {
        success: true,
        data: response?.data,
        message: response?.data?.message,
      };
    } catch (e: any) {
      setLoading(false);
      return {
        success: false,
        message: e?.response?.data?.message,
      };
    } finally {
      setLoading(false);
    }
  };

  const getBlocksData = async (payload: IPayloadType): Promise<ApiResponseType<any>> => {
    try {
      const isoStartDate = payload?.blocksFromDate ? moment(payload?.blocksFromDate).format('YYYY-MM-DDTHH:mm:ss[Z]') : null;
      const isoEndDate = payload?.blocksToDate ? moment(payload?.blocksToDate).add(1, 'day').startOf('day').format('YYYY-MM-DDTHH:mm:ss[Z]') : null;
      setLoading(true);

      let url = (isoStartDate && isoEndDate)
        ? `/blockAndTxList?from=${isoStartDate}&to=${isoEndDate}&page=${payload?.PageNumber}&size=${payload?.Size}`
        : `/blockAndTxList?page=${payload?.PageNumber}&size=${payload?.Size}`;

      if (payload?.search) {
        url += `&searchParam=${payload?.search}`;
      }
      const response = await axiosInstance.get(url);
      if(mobile) {
        setBlocksData(payload?.PageNumber === 1 ? response?.data?.data : [...blocksData, ...response?.data?.data])
      } else {
        setBlocksData(response?.data?.data);
      }
      setIsTotalPages(response?.data?.totalPages);
      setTotalCount(response?.data?.totalBlocks);
      setLoading(false);
      return {
        success: true,
        data: response?.data,
        message: response?.data?.message,
      };
    } catch (e: any) {
      setLoading(false);
      return {
        success: false,
        message: e?.response?.data?.message,
      };
    } finally {
      setLoading(false);
    }
  };

  const getTransactionCountByBlockNum = async (blocknum: string): Promise<ApiResponseType<any>> => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/block/${blocknum}/transactionCount`);

      setBlockDetailsCount(response?.data)
      return {
        success: true,
        data: response?.data,
        message: response?.data?.message,
      };
    } catch (e: any) {
      setLoading(false);
      return {
        success: false,
        message: e?.response?.data?.message,
      };
    } finally {
      setLoading(false);
    }
  };

  const getTransactionsByBlockId = async (payload: IPayloadType): Promise<ApiResponseType<any>> => {
    try {
      const isoStartDate = payload?.blocksFromDate ? moment(payload?.blocksFromDate).format('YYYY-MM-DDTHH:mm:ss[Z]') : null;
      const isoEndDate = payload?.blocksToDate ? moment(payload?.blocksToDate).add(1, 'day').startOf('day').format('YYYY-MM-DDTHH:mm:ss[Z]'): null;
      setLoading(true);

      let url = (isoStartDate && isoEndDate)
        ? `/txList?blockNum=${payload?.blockId}&from=${isoStartDate}&to=${isoEndDate}&page=${payload?.PageNumber}&size=${payload?.Size}`
        : `/txList?blockNum=${payload?.blockId}&page=${payload?.PageNumber}&size=${payload?.Size}`;

      if (payload?.search) {
        url += `&singleSearch=${payload?.search}`;
      }

      const response = await axiosInstance.get(url);
      if(mobile) {
        setTransactionsByBlockId(payload?.PageNumber === 1 ? response?.data?.rows?.txnsData : [...transactionsByBlockId, ...response?.data?.rows?.txnsData])
      } else {
        setTransactionsByBlockId(response?.data?.rows?.txnsData);
      }
      setIsIdTotalPages(response?.data?.rows?.noOfPages);
      setTotalCount(response?.data?.rows?.countOfTxns)
      return {
        success: true,
        data: response?.data,
        message: response?.data?.message,
      };
    } catch (e: any) {
      setLoading(false);
      return {
        success: false,
        message: e?.response?.data?.message,
      };
    } finally {
      setLoading(false);
    }
  };

  const contextValue = {
    loading,
    blocksData,
    getBlocksData,
    isTotalPages,
    getTransactionCountByBlockNum,
    getTransactionsByBlockId,
    getBlocksDataPerMinute,
    blockCountPerMinute,
    blockDetailsCount,
    transactionsByBlockId,
    isIdTotalPages,
    totalCount
  };

  return (
    <BlocksContext.Provider value={contextValue}>{children}</BlocksContext.Provider>
  );
};
